import { formatText, useTexts, type Label } from "../texts";
import { PageHead } from "../ui/kit";
import { CodeBlock, PageState, ServiceState } from "../ui/pieces";
import { attemptsMissing, attemptsText, duration, executionTone, money, number, operationName, when } from "./ai";
import { executionsBackKey } from "./ExecutionsPage";
import { useAiData, useTitle } from "./parts";
import { Indicator, IndicatorSkeleton, Indicators, Mark, RowMenu, Section, useIsMobile } from "./widgets";
import styles from "./ai.module.css";

type Execution = {
  executionId: number;
  organizationId: number | null;
  operationType: string;
  status: string;
  attemptCount: number;
  totalInputTokens: number | null;
  totalOutputTokens: number | null;
  totalCost: number | null;
  currency: string | null;
  latencyMilliseconds: number | null;
  startedAt: string;
};

type Attempt = {
  attemptNumber: number;
  status: string;
  providerModelCode: string;
  providerName: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  latencyMs: number | null;
  estimatedCost: number | null;
  currency: string | null;
  errorCode: string | null;
  requestContent: string;
  responseContent: string | null;
};

/** El motivo de un error en palabras, si se conoce; el código va aparte (D7: sirve para investigar). */
const errorReason = (L: Label, code: string | null) =>
  !code
    ? null
    : /TIMEOUT/i.test(code)
      ? L("ADM_AI_ERROR_TIMEOUT")
      : /AUTH|401|403/i.test(code)
        ? L("ADM_AI_ERROR_AUTH")
        : null;

/** ← vuelve a Ejecuciones con su filtro (el último que se vio en esta pestaña). */
function backHref() {
  try {
    const saved = sessionStorage.getItem(executionsBackKey);
    return saved ? `/Ai/Uso/Ejecuciones?${saved}` : "/Ai/Uso/Ejecuciones";
  } catch {
    return "/Ai/Uso/Ejecuciones";
  }
}

/**
 * IA › Uso y costos › ← Ejecución (§5.9 `ia-traza`): indicadores Tokens, Costo, Tiempo e Intentos; los intentos con
 * proveedor, modelo, resultado, tiempo y, si falló, el código del error; la petición y la respuesta en bloques de código
 * con «Copiar» (C53). Los ids internos no se muestran (D7).
 */
export function UsageDetailPage({ id }: { id: number }) {
  const { L, locale } = useTexts();
  const mobile = useIsMobile();
  const { data, state, retry } = useAiData<{
    execution: Execution;
    attempts: Attempt[];
    organizationName: string | null;
  }>(`/api/admin/ai/usage/${id}`);
  const title = formatText(L("ADM_AI_EXECUTION_TITLE"), `#${id}`);
  useTitle(title);
  const back = { href: backHref(), label: L("ADM_AI_EXECUTIONS") };
  if (state === "missing" || state === "failed")
    return (
      <PageState
        head={<PageHead title={title} back={back} />}
        title={L(state === "missing" ? "ADM_AI_EXECUTION_MISSING" : "ADM_UNAVAILABLE")}
        line={L(state === "missing" ? "ADM_AI_EXECUTION_MISSING_LINE" : "ADM_UNAVAILABLE_LINE")}
        action={
          state === "missing" ? (
            <a className="tm-btn secondary" href="/Ai/Uso/Ejecuciones">
              {L("ADM_AI_EXECUTIONS")}
            </a>
          ) : (
            <button type="button" className="tm-btn secondary" onClick={retry}>
              {L("ADM_RETRY")}
            </button>
          )
        }
      />
    );
  if (!data)
    return (
      <>
        <PageHead title={title} back={back} />
        <IndicatorSkeleton count={4} label={title} />
      </>
    );

  const run = data.execution;
  const tone = executionTone(L, run.status);
  const attempts = [...data.attempts].sort((a, b) => a.attemptNumber - b.attemptNumber);
  const failed = attempts.filter((attempt) => attempt.status === "ERROR").length;
  const answered = attempts.find((attempt) => attempt.status === "SUCCESS") ?? attempts.at(-1) ?? null;
  const organization =
    run.organizationId == null ? L("ADM_AI_PLATFORM") : (data.organizationName ?? L("ADM_AI_ORG_OUT_OF_SCOPE"));
  const line = (attempt: Attempt) => {
    const parts = [String(attempt.attemptNumber), attempt.providerModelCode];
    if (attempt.status === "ERROR") {
      const reason = errorReason(L, attempt.errorCode);
      if (reason) parts.push(reason);
      parts.push(duration(L, locale, attempt.latencyMs));
      if (attempt.errorCode) parts.push(formatText(L("ADM_AI_ERROR_CODE"), attempt.errorCode));
    } else {
      parts.push(duration(L, locale, attempt.latencyMs));
      parts.push(
        formatText(
          L("ADM_AI_TOKENS_PAIR"),
          (attempt.inputTokens ?? 0).toLocaleString(locale),
          (attempt.outputTokens ?? 0).toLocaleString(locale),
        ),
      );
      parts.push(money(locale, attempt.estimatedCost, attempt.currency));
    }
    return parts.join(" · ");
  };

  return (
    <>
      <PageHead
        title={title}
        back={back}
        tag={<ServiceState tone={tone.tone}>{tone.text}</ServiceState>}
        sub={`${operationName(L, run.operationType)} · ${organization} · ${when(L, locale, run.startedAt)}`}
      >
        {run.organizationId != null && data.organizationName ? (
          mobile ? (
            <RowMenu
              label={L("ADM_MORE_ACTIONS")}
              title={title}
              items={[
                {
                  label: L("ADM_AI_VIEW_ORG"),
                  onSelect: () => window.location.assign(`/Organizations/Detail?id=${run.organizationId}`),
                },
              ]}
            />
          ) : (
            <a className="tm-btn secondary" href={`/Organizations/Detail?id=${run.organizationId}`}>
              {L("ADM_AI_VIEW_ORG")}
            </a>
          )
        ) : null}
      </PageHead>
      <Indicators label={title} count={4}>
        <Indicator
          label={L("ADM_AI_COL_TOKENS")}
          value={
            run.totalInputTokens == null && run.totalOutputTokens == null
              ? "—"
              : `${number(locale, run.totalInputTokens)} / ${number(locale, run.totalOutputTokens)}`
          }
          small
          line={L("ADM_AI_TOKENS_IO_LINE")}
        />
        <Indicator
          label={L("ADM_AI_COST")}
          value={money(locale, run.totalCost, run.currency)}
          small
          muted={run.totalCost == null}
          line={L(run.totalCost == null ? "ADM_AI_NO_PRICE_LINE" : "ADM_AI_COST_CURRENT_PRICE")}
        />
        <Indicator
          label={L("ADM_AI_COL_TIME")}
          value={duration(L, locale, answered?.latencyMs ?? run.latencyMilliseconds)}
          small
          line={L("ADM_AI_TIME_LINE")}
        />
        <Indicator
          label={L("ADM_AI_COL_ATTEMPTS")}
          value={attemptsText(locale, run.status, run.attemptCount)}
          small
          muted={attemptsMissing(run.status, run.attemptCount)}
          line={
            attemptsMissing(run.status, run.attemptCount)
              ? L("ADM_AI_ATTEMPTS_NOT_RECORDED")
              : failed === 0
                ? L("ADM_AI_NO_ERRORS")
                : formatText(
                    L(failed === 1 ? "ADM_AI_N_WITH_ERROR_ONE" : "ADM_AI_N_WITH_ERROR"),
                    failed.toLocaleString(locale),
                  )
          }
        />
      </Indicators>
      <Section id="trace-attempts" title={L("ADM_AI_ATTEMPTS")}>
        {attempts.length === 0 ? (
          <div className="tm-empty" role="status">
            <strong>{L("ADM_AI_NO_ATTEMPTS")}</strong>
            <span>
              {L(
                attemptsMissing(run.status, run.attemptCount)
                  ? "ADM_AI_ATTEMPTS_NOT_RECORDED_LINE"
                  : "ADM_AI_NO_ATTEMPTS_LINE",
              )}
            </span>
          </div>
        ) : (
          <ul className={styles.list}>
            {attempts.map((attempt) => {
              const attemptTone = executionTone(L, attempt.status);
              const name = attempt.providerName ?? L("USAGE_UNKNOWN");
              return (
                <li key={attempt.attemptNumber} className={styles.row}>
                  <Mark name={name} />
                  <span className={styles.rowText}>
                    <span className={styles.line1}>
                      <strong>{name}</strong>
                      <ServiceState tone={attemptTone.tone}>{attemptTone.text}</ServiceState>
                    </span>
                    <span className={styles.line2}>{line(attempt)}</span>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Section>
      {answered ? (
        <div className={styles.windowBlock}>
          <CodeBlock title={L("ADM_AI_REQUEST")} value={answered.requestContent} />
          {answered.responseContent ? (
            <CodeBlock title={L("ADM_SAMPLE_RESPONSE")} value={answered.responseContent} />
          ) : null}
        </div>
      ) : null}
    </>
  );
}
